import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Phone, MessageCircle, ChevronDown, ChevronUp,
  Clock, ArrowRight, Star,
} from 'lucide-react';
import SEOHead from './SEOHead';
import ContactFormWidget from './ContactFormWidget';
import { PHONE, PHONE_LINK, WHATSAPP, ADDRESS, DOMAIN } from '../constants';

export default function ServicePageLayout({ data }) {
  const [openFaq, setOpenFaq] = useState(null);
  const {
    seo, schemas = [], hero, serviceCard, benefits, process: steps,
    pricing, faq, relatedServices, serviceName, formTitle,
  } = data;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Startseite', 'item': DOMAIN },
      { '@type': 'ListItem', 'position': 2, 'name': seo.h1short, 'item': `${DOMAIN}${seo.canonical}` },
    ],
  };
  const faqSchema = faq?.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faq.map(f => ({
      '@type': 'Question',
      'name': f.q,
      'acceptedAnswer': { '@type': 'Answer', 'text': f.a },
    })),
  } : null;

  const allSchemas = [...schemas, breadcrumbSchema, ...(faqSchema ? [faqSchema] : [])];

  return (
    <>
      <SEOHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={seo.canonical}
        extraSchemas={allSchemas}
      />
      <main>
        {/* BREADCRUMB */}
        <nav className="bg-[#FAFAF8] border-b border-gray-200 py-2 px-4" aria-label="Breadcrumb">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center gap-2 text-xs text-gray-500">
              <li><Link to="/" className="hover:text-[#E8722A] transition-colors">Startseite</Link></li>
              <li className="text-gray-300">/</li>
              <li className="text-[#1A1A2E] font-semibold">{seo.h1short}</li>
            </ol>
          </div>
        </nav>

        {/* HERO */}
        <section
          className="relative flex items-center"
          style={{
            backgroundImage: `url('${hero.imageSrc}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            minHeight: '88vh',
          }}
        >
          <div className="absolute inset-0 bg-[#1A1A2E]/85" />
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-14 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div className="text-white">
                <div className="inline-flex items-center gap-2 bg-[#E8722A] text-white px-4 py-2 rounded text-xs font-black mb-5 uppercase tracking-wider">
                  <Clock size={13} /> {hero.badge}
                </div>
                <h1 className="cabinet text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4">
                  {hero.headline}
                </h1>
                <p className="text-lg text-gray-200 font-semibold mb-1">{hero.subline}</p>
                <div className="space-y-2.5 mb-8">
                  {hero.bullets.map((b) => (
                    <div key={b} className="flex items-start gap-2.5">
                      <CheckCircle size={18} className="text-[#E8722A] flex-shrink-0 mt-0.5" />
                      <span className="text-white/90 text-sm leading-snug">{b}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  <a href={PHONE_LINK}
                    className="flex items-center gap-2 bg-white text-[#1A1A2E] font-black px-6 py-3.5 rounded-lg hover:bg-gray-100 transition-colors text-sm">
                    <Phone size={16} /> Jetzt anrufen
                  </a>
                  <a href={WHATSAPP} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-[#25D366] text-white font-black px-6 py-3.5 rounded-lg hover:bg-[#20bd5a] transition-colors text-sm">
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                </div>
              </div>
              <div id="kontakt" className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
                <div className="mb-5">
                  <h2 className="cabinet font-bold text-[#1A1A2E] leading-tight mb-1" style={{ fontSize: '1.15rem', letterSpacing: '-0.01em' }}>
                    {formTitle || 'Kostenlos anfragen –'} <span className="text-[#E8722A]">in 2 Minuten</span>
                  </h2>
                  <p className="text-gray-500 text-xs">Kein Risiko. Kostenlose Besichtigung. Verbindlicher Fixpreis.</p>
                </div>
                <ContactFormWidget serviceName={serviceName} />
              </div>
            </div>
          </div>
        </section>

        {/* TRUST BAR */}
        <section className="bg-[#FAFAF8] border-y border-gray-200 py-4">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-200">
              {[
                { val: '⭐ 4.9 / 5.0', label: 'Google Bewertung' },
                { val: '2.000+ Kunden', label: 'Zufrieden seit 2010' },
                { val: 'Fixpreisgarantie', label: 'Keine Nachforderungen' },
                { val: '24h Service', label: 'Notfälle bevorzugt' },
              ].map((t) => (
                <div key={t.label} className="py-3 px-4 text-center">
                  <div className="font-black text-[#1A1A2E] text-base sm:text-lg leading-tight">{t.val}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{t.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICE CARD */}
        <section className="py-14 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-3">Professionell &amp; zuverlässig</p>
                <h2 className="cabinet text-3xl lg:text-4xl font-bold text-[#1A1A2E] mb-5 leading-tight">
                  {serviceCard.heading}
                </h2>
                {serviceCard.paragraphs.map((p, i) => (
                  <p key={i} className="text-gray-600 text-sm leading-relaxed mb-4">{p}</p>
                ))}
                <div className="space-y-2.5 mt-6 mb-8">
                  {serviceCard.listItems.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle size={17} className="text-[#E8722A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <a href={PHONE_LINK}
                  className="inline-flex items-center gap-2 bg-[#E8722A] hover:bg-[#d4651e] text-white font-black px-7 py-3.5 rounded-lg transition-colors text-sm">
                  <Phone size={15} /> {PHONE}
                </a>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl h-80 lg:h-[480px]">
                <img src={serviceCard.imageSrc} alt={serviceCard.imageAlt}
                  className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        {benefits?.length > 0 && (
          <section className="py-14 lg:py-20 bg-[#FAFAF8]">
            <div className="max-w-7xl mx-auto px-4">
              <div className="text-center mb-10">
                <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-2">Unsere Vorteile</p>
                <h2 className="cabinet text-3xl font-bold text-[#1A1A2E]">Warum Kunden uns wählen</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {benefits.map((b) => (
                  <div key={b.title} className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-[#E8722A] hover:shadow-md transition-all">
                    <div className="text-[#E8722A] mb-3">{b.icon}</div>
                    <h3 className="font-bold text-[#1A1A2E] mb-2 text-base">{b.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* PROCESS */}
        {steps?.length > 0 && (
          <section className="py-16 lg:py-24 bg-white">
            <div className="max-w-[900px] mx-auto px-4">
              <div className="text-center mb-8 lg:mb-14">
                <p className="text-[#E8722A] font-bold text-xs uppercase tracking-widest">EINFACH &amp; TRANSPARENT</p>
                <h2 className="font-bold text-2xl lg:text-3xl text-[#1A1A2E] mt-2">So läuft es ab</h2>
              </div>
              <div className="relative">
                {/* Desktop horizontal connecting line */}
                <div
                  className="hidden md:block absolute h-0.5 bg-[#E8722A] z-0 pointer-events-none"
                  style={{ top: '32px', left: 'calc(16.67% + 32px)', right: 'calc(16.67% + 32px)' }}
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                  {steps.map((s, i) => (
                    <div key={i}
                      className="flex md:flex-col items-center gap-4 md:gap-0 text-left md:text-center relative z-10 bg-[#FAFAF8] md:bg-white border border-gray-100 md:border-0 rounded-2xl md:rounded-none p-4 md:p-0"
                      data-testid={`process-step-${i}`}>
                      <div className="flex-shrink-0 group w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-[#E8722A] bg-white flex items-center justify-center transition-all duration-300 hover:bg-[#E8722A] cursor-default relative z-10">
                        <div className="text-[#E8722A] group-hover:text-white transition-colors duration-300">
                          {s.icon}
                        </div>
                      </div>
                      <div>
                        <p className="text-[#E8722A] text-xs font-bold mb-0.5 md:mt-4">
                          {String(i + 1).padStart(2, '0')}
                        </p>
                        <h3 className="font-bold text-base md:text-xl text-[#1A1A2E] md:mt-2">{s.title}</h3>
                        <p className="text-sm text-gray-500 mt-1 md:mt-2 leading-snug">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-center mt-12">
                <a
                  href="#kontakt"
                  onClick={(e) => { e.preventDefault(); document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="bg-[#E8722A] text-white font-bold py-3 px-8 rounded-lg hover:scale-[1.02] transition-transform inline-block">
                  Kostenlos anfragen →
                </a>
              </div>
            </div>
          </section>
        )}

        {/* PRICING */}
        {pricing?.length > 0 && (
          <section className="py-14 lg:py-20 bg-[#FAFAF8]">
            <div className="max-w-3xl mx-auto px-4">
              <div className="text-center mb-8">
                <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-2">Transparente Kalkulation</p>
                <h2 className="cabinet text-3xl font-bold text-[#1A1A2E]">Richtwerte &amp; Preise</h2>
                <p className="text-gray-500 text-sm mt-2">Verbindlicher Fixpreis nach kostenloser Besichtigung.</p>
              </div>
              <div className="space-y-3.5 mb-8">
                {pricing.map((pr) => (
                  <div key={pr.label}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all hover:-translate-y-0.5 ${
                      pr.highlight ? 'border-[#E8722A] bg-[#E8722A]/5 shadow-sm' : 'border-gray-100 bg-white'
                    }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${pr.highlight ? 'bg-[#E8722A] text-white' : 'bg-[#1A1A2E]/10 text-[#1A1A2E]'}`}>
                        {pr.icon}
                      </div>
                      <div>
                        <div className="font-black text-[#1A1A2E] text-sm">{pr.label}</div>
                        <div className="text-gray-400 text-xs flex items-center gap-1">
                          <CheckCircle size={11} className="text-green-500" /> Besenrein &amp; Entsorgung inklusive
                        </div>
                      </div>
                    </div>
                    <div className="font-black text-2xl sm:text-4xl text-[#E8722A] leading-none">{pr.price}</div>
                  </div>
                ))}
              </div>
              <a href="#kontakt"
                onClick={(e) => { e.preventDefault(); document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="block w-full text-center bg-[#E8722A] hover:bg-[#d4651e] text-white font-black py-4 rounded-xl transition-colors text-base">
                Preis für Ihr Objekt anfragen
              </a>
              <p className="text-xs text-gray-400 mt-3 text-center">* Exakter Fixpreis nach kostenloser Besichtigung.</p>
            </div>
          </section>
        )}

        {/* REVIEWS MINI */}
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { name: 'Maria K.', loc: '1030 Wien', text: 'Schnell, sauber und fair. Wohnung meiner Mutter in einem Tag geräumt. Keine Überraschungen beim Preis.', stars: 5 },
                { name: 'Thomas B.', loc: '1100 Wien', text: 'Endlich ein Unternehmen das hält was es verspricht. Fixpreis eingehalten, alles besenrein übergeben.', stars: 5 },
                { name: 'Andrea M.', loc: '1210 Wien', text: 'Professionell und diskret. Kein Aufpreis, super Arbeit. Danke für das Einfühlungsvermögen!', stars: 5 },
              ].map((r) => (
                <div key={r.name} className="bg-[#FAFAF8] rounded-2xl p-6 border border-gray-100">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: r.stars }).map((_, i) => (
                      <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 text-sm italic mb-4">"{r.text}"</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#1A1A2E] rounded-full flex items-center justify-center text-white text-xs font-black">
                      {r.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-black text-[#1A1A2E] text-sm">{r.name}</div>
                      <div className="text-gray-400 text-xs">{r.loc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        {faq?.length > 0 && (
          <section className="py-14 lg:py-20 bg-[#FAFAF8]" data-testid="faq-section">
            <div className="max-w-3xl mx-auto px-4">
              <div className="text-center mb-10">
                <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-2">Häufige Fragen</p>
                <h2 className="cabinet text-3xl font-bold text-[#1A1A2E]">FAQ – {seo.h1short}</h2>
              </div>
              <div className="space-y-2">
                {faq.map((item, idx) => (
                  <div key={idx} className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={`faq-item-${idx}`}>
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-[#FAFAF8]/70 transition-colors">
                      <span className="font-black text-[#1A1A2E] text-sm sm:text-base leading-snug">{item.q}</span>
                      {openFaq === idx
                        ? <ChevronUp size={18} className="text-[#E8722A] flex-shrink-0" />
                        : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
                    </button>
                    <div className={`faq-content ${openFaq === idx ? 'open' : ''}`}>
                      <div className="px-6 pb-5 border-t border-gray-50">
                        <p className="text-gray-500 text-sm leading-relaxed pt-4">{item.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* RELATED SERVICES */}
        {relatedServices?.length > 0 && (
          <section className="py-12 bg-white">
            <div className="max-w-5xl mx-auto px-4">
              <h2 className="text-xl font-bold text-[#1A1A2E] mb-6 text-center">Weitere Leistungen in Wien</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {relatedServices.map((s) => (
                  <Link key={s.slug} to={s.slug}
                    className="flex items-center gap-3 bg-[#FAFAF8] rounded-xl p-4 border-2 border-gray-100 hover:border-[#E8722A] hover:shadow-sm transition-all">
                    <div className="text-[#1A1A2E]">{s.icon}</div>
                    <span className="font-bold text-[#1A1A2E] text-sm">{s.title}</span>
                    <ArrowRight size={14} className="text-[#E8722A] ml-auto flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FINAL CTA */}
        <section className="py-14 lg:py-20 bg-[#FAFAF8]">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="cabinet text-3xl sm:text-4xl font-bold text-[#1A1A2E] mb-5 leading-tight">
              Räumung nötig?{' '}
              <span className="text-[#E8722A]">Wir sind morgen da.</span>
            </h2>
            <div className="flex flex-wrap justify-center gap-4 mb-8 text-gray-500 text-sm">
              {['Kostenlose Besichtigung', 'Fixpreisgarantie', 'Besenreine Übergabe'].map((t) => (
                <span key={t} className="flex items-center gap-2">
                  <CheckCircle size={15} className="text-[#7C9A82]" />
                  <span className="font-semibold">{t}</span>
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="#kontakt"
                onClick={(e) => { e.preventDefault(); document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="bg-[#E8722A] hover:bg-[#d4651e] text-white font-black px-10 py-4 rounded-xl text-lg transition-colors shadow-md">
                Jetzt kostenlos anfragen
              </a>
              <a href={PHONE_LINK}
                className="flex items-center justify-center gap-2.5 text-[#1A1A2E] border-2 border-gray-300 hover:border-[#E8722A] font-black px-8 py-4 rounded-xl text-lg transition-colors">
                <Phone size={20} /> {PHONE}
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
